// Minimal Telegram Mini App bridge: transports plus the few methods the app
// needs. Unlike the official telegram-web-app.js it never touches the DOM,
// styles or theme and ignores client styling events such as set_css.
import getMiniappInitData from "./telegramMiniapp";

const initParams = getMiniappInitData();
const version = initParams.tgWebAppVersion || "6.0";
const platform = initParams.tgWebAppPlatform || "unknown";
const isIframe = (() => {
  try {
    return window.self !== window.parent;
  } catch (e) {
    return false;
  }
})();

const eventCallbacks = {};

const onEvent = (eventType, callback) => {
  (eventCallbacks[eventType] = eventCallbacks[eventType] || []).push(callback);
};

const offEvent = (eventType, callback) => {
  const list = eventCallbacks[eventType] || [];
  const index = list.indexOf(callback);
  if (index !== -1) {
    list.splice(index, 1);
  }
};

const receiveEvent = (eventType, eventData) => {
  (eventCallbacks[eventType] || []).slice().forEach((cb) => {
    cb(eventType, eventData);
  });
};

// Clients deliver events back through these globals (mobile apps) or through
// window.parent messages (Telegram Web).
window.TelegramGameProxy_receiveEvent = receiveEvent;
window.TelegramGameProxy = { receiveEvent };

const postEvent = (eventType, eventData = "") => {
  if (window.TelegramWebviewProxy !== undefined) {
    window.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
  } else if (window.external && "notify" in window.external) {
    window.external.notify(JSON.stringify({ eventType, eventData }));
  } else if (isIframe) {
    try {
      window.parent.postMessage(
        JSON.stringify({ eventType, eventData }),
        "*"
      );
    } catch (e) {}
  }
};

if (isIframe) {
  window.addEventListener("message", (event) => {
    let dataParsed;
    try {
      dataParsed = JSON.parse(event.data);
    } catch (e) {
      return;
    }
    if (dataParsed && typeof dataParsed === "object" && dataParsed.eventType) {
      receiveEvent(dataParsed.eventType, dataParsed.eventData);
    }
  });
  try {
    window.parent.postMessage(
      JSON.stringify({
        eventType: "iframe_ready",
        eventData: { reload_supported: true },
      }),
      "*"
    );
  } catch (e) {}
}

const versionParts = (value) =>
  String(value)
    .split(".")
    .map((part) => parseInt(part, 10) || 0);

const versionAtLeast = (target) => {
  const current = versionParts(version);
  const required = versionParts(target);
  for (let i = 0; i < Math.max(current.length, required.length); i++) {
    const diff = (current[i] || 0) - (required[i] || 0);
    if (diff !== 0) {
      return diff > 0;
    }
  }
  return true;
};

// CloudStorage / invokeCustomMethod (Bot API >= 6.9): запрос через
// web_app_invoke_custom_method, ответ приходит событием custom_method_invoked.
const customMethodCallbacks = {};
let customMethodSeq = 0;

const invokeCustomMethod = (method, params, callback) => {
  if (!versionAtLeast("6.9")) {
    if (typeof callback === "function") {
      callback(Error("WebAppMethodUnsupported"));
    }
    return;
  }
  customMethodSeq += 1;
  const reqId = `bridge_${Date.now().toString(36)}_${customMethodSeq}`;
  customMethodCallbacks[reqId] = callback;
  postEvent("web_app_invoke_custom_method", {
    req_id: reqId,
    method,
    params: params || {},
  });
};

const onCustomMethodInvoked = (eventType, eventData) => {
  const reqId = eventData?.req_id;
  const callback = reqId && customMethodCallbacks[reqId];
  if (!callback) {
    return;
  }
  delete customMethodCallbacks[reqId];
  callback(
    typeof eventData.error === "undefined" ? null : eventData.error,
    typeof eventData.result === "undefined" ? null : eventData.result
  );
};
onEvent("custom_method_invoked", onCustomMethodInvoked);

const cloudStorage = {
  setItem(key, value, callback) {
    invokeCustomMethod("saveStorageValue", { key, value }, callback);
    return cloudStorage;
  },
  getItem(key, callback) {
    cloudStorage.getItems(
      [key],
      typeof callback === "function"
        ? (err, res) => {
            if (err) {
              callback(err);
            } else {
              callback(null, res ? res[key] : null);
            }
          }
        : null
    );
    return cloudStorage;
  },
  getItems(keys, callback) {
    invokeCustomMethod("getStorageValues", { keys }, callback);
    return cloudStorage;
  },
  removeItem(key, callback) {
    cloudStorage.removeItems([key], callback);
    return cloudStorage;
  },
  removeItems(keys, callback) {
    invokeCustomMethod("deleteStorageValues", { keys }, callback);
    return cloudStorage;
  },
};

// Реальные параметры темы клиент кладёт в hash (tgWebAppThemeParams).
// telegram-ui читает themeParams.bg_color / colorScheme и дергает
// onEvent('themeChanged') без защит — без этих полей страницы с Modal
// падали с TypeError («An Error Occurred»).
const parseThemeParams = () => {
  try {
    const parsed = JSON.parse(initParams.tgWebAppThemeParams || "");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const themeParams = parseThemeParams();

const isColorDark = (hex) => {
  try {
    let rgb = String(hex || "").replace(/[\s#]/g, "");
    if (rgb.length === 3) {
      rgb = rgb[0] + rgb[0] + rgb[1] + rgb[1] + rgb[2] + rgb[2];
    }
    const r = parseInt(rgb.substr(0, 2), 16);
    const g = parseInt(rgb.substr(2, 2), 16);
    const b = parseInt(rgb.substr(4, 2), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return false;
    }
    return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b)) < 120;
  } catch (e) {
    return false;
  }
};

const colorScheme = isColorDark(themeParams.bg_color) ? "dark" : "light";

const webApp = {
  version,
  platform,
  initData: initParams.tgWebAppData || "",
  themeParams,
  colorScheme,
  onEvent,
  offEvent,
  ready() {
    postEvent("web_app_ready");
  },
  openLink(url) {
    const a = document.createElement("A");
    a.href = url;
    if (a.protocol !== "http:" && a.protocol !== "https:") {
      throw Error("WebAppTgUrlInvalid");
    }
    if (versionAtLeast("6.1")) {
      postEvent("web_app_open_link", { url: a.href });
    } else {
      window.open(a.href, "_blank");
    }
  },
  downloadFile(params, callback) {
    if (!versionAtLeast("8.0")) {
      throw Error("WebAppMethodUnsupported");
    }
    if (!params || !params.url || !params.url.length) {
      throw Error("WebAppDownloadFileParamInvalid");
    }
    const a = document.createElement("A");
    a.href = params.url;
    if (a.protocol !== "https:") {
      throw Error("WebAppDownloadFileParamInvalid");
    }
    if (!params.file_name || !params.file_name.length) {
      throw Error("WebAppDownloadFileParamInvalid");
    }
    postEvent("web_app_request_file_download", {
      url: a.href,
      file_name: params.file_name,
    });
    if (typeof callback === "function") {
      const handler = (eventType, eventData) => {
        offEvent("file_download_requested", handler);
        callback(eventData?.status === "downloading");
      };
      onEvent("file_download_requested", handler);
    }
  },
};

// Fill in what the client-injected object is missing instead of replacing it.
// CloudStorage подключается только в реальном окружении Telegram (есть
// нативный WebApp или подписанный initData в hash): в браузере postEvent
// никуда не доставляет события, поэтому хранилище там не объявляем.
window.Telegram = window.Telegram || {};
if (window.Telegram.WebApp) {
  const native = window.Telegram.WebApp;
  if (typeof native.ready !== "function") {
    native.ready = webApp.ready;
  }
  if (typeof native.openLink !== "function") {
    native.openLink = webApp.openLink;
  }
  if (typeof native.downloadFile !== "function") {
    native.downloadFile = webApp.downloadFile;
  }
  if (!native.themeParams) {
    native.themeParams = webApp.themeParams;
  }
  if (!native.colorScheme) {
    native.colorScheme = webApp.colorScheme;
  }
  if (typeof native.onEvent !== "function") {
    native.onEvent = webApp.onEvent;
  }
  if (typeof native.offEvent !== "function") {
    native.offEvent = webApp.offEvent;
  }
  if (!native.CloudStorage) {
    native.CloudStorage = cloudStorage;
  }
} else if (initParams.tgWebAppData) {
  window.Telegram.WebApp = { ...webApp, CloudStorage: cloudStorage };
} else {
  window.Telegram.WebApp = webApp;
}

export default webApp;
